// ============================================================================
// Geographic Widget — Active facilities grouped by market_region with a
// horizontal bar chart of the count and an MRR readout per market.
//
// We deliberately don't render an embedded Google Map here even though
// facilities have lat/lng. Reasons:
//   * Account Management is the canonical map view (Phase 2 of that module).
//   * A map at dashboard scale would be either too small to read or too big
//     to fit the widget grid.
//   * Bars convey "where is our footprint concentrated?" faster than pins.
//
// If a real map ever ships here, the data shape (market + count + mrr) is
// already enough to drop pins; we'd just swap the bars for a map widget.
// ============================================================================

import type { GeographicBucket } from './use-dashboard-data';
import {
  WidgetCard,
  WidgetSkeleton,
  fmtCompactCurrency,
} from './widget-card';

interface Props {
  data: GeographicBucket[] | null;
  loading: boolean;
}

export function GeographicWidget({ data, loading }: Props) {
  if (loading && !data) {
    return (
      <WidgetCard title="Geography" minHeight={300}>
        <WidgetSkeleton lines={6} />
      </WidgetCard>
    );
  }

  if (!data) {
    return (
      <WidgetCard title="Geography" minHeight={300}>
        <p className="text-sm text-tcs-text-muted">No geographic data available.</p>
      </WidgetCard>
    );
  }

  const totalFacilities = data.reduce((s, b) => s + b.count, 0);
  const maxCount = Math.max(1, ...data.map((b) => b.count));

  return (
    <WidgetCard
      title="Geography"
      subtitle={`${totalFacilities} active facilities · ${data.length} ${data.length === 1 ? 'market' : 'markets'}`}
      minHeight={300}
    >
      {data.length === 0 ? (
        <p className="text-sm text-tcs-text-muted">No active facilities.</p>
      ) : (
        <ul className="space-y-1.5">
          {data.map((b) => {
            const widthPct = (b.count / maxCount) * 100;
            const sharePct = totalFacilities > 0
              ? Math.round((b.count / totalFacilities) * 100)
              : 0;
            return (
              <li key={b.market} className="text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-tcs-text-secondary">{b.market}</span>
                  <span className="shrink-0 text-xs tabular-nums text-tcs-text-muted">
                    {b.count}
                    <span className="ml-1 text-tcs-text-muted">
                      · {sharePct}% · {fmtCompactCurrency(b.mrr)}/mo
                    </span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-sm bg-tcs-surface">
                  <div
                    className="h-full rounded-sm bg-tcs-inverse"
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetCard>
  );
}
